import React, { useEffect, useState } from 'react';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import crypto from 'crypto-js';
import API from '@/amplify/api';
import { downloadFileFromS3 } from './UploadFileMessage';
import { getPublicAPI } from '@/service/publicService';
import { CircularProgress } from '@material-ui/core';

const getLinkFileFromS3Path = (s3Path) => {
    if (Utils.isEmptyString(s3Path)) {
        return '';
    }

    const uriObj = {
        projectList: [],
        orgHasReadPermission: [],
        userHasReadPermission: [],
        keyFile: `/public/${s3Path}`,
        publicAll: true
    };

    const enCodeUriObj = encodeURIComponent(
        crypto.AES.encrypt(JSON.stringify(uriObj), Constant.S3_URL_AES_ENCODE_KEY).toString()
    );
    const newUri = `/${enCodeUriObj}`;
    return `${process.env.REACT_APP_CLOUD_FRONT_URL}${newUri}`;
};

const getFileSizeDisplay = (totalByte) => {
    if (!totalByte) return '0';
    if (totalByte < 1024) {
        return `${totalByte} bytes`;
    }
    let num = totalByte / 1024;
    if (totalByte < 1024 * 1024) {
        return `${Math.round(num * 100) / 100} KB`;
    }
    num = num / 1024;
    if (totalByte < 1024 * 1024 * 1024) {
        return `${Math.round(num * 100) / 100} MB`;
    }
    num = num / 1024;
    return `${Math.round(num * 100) / 100} GB`;
};

const isImageFile = (fileName) => {
    if (!fileName) return false;
    const ext = fileName.split('.').pop()?.toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif', 'svg', 'webp'];
    return imageExtensions.includes(ext);
};

function MsgFilesMessage({ message }) {
    const [attachFiles, setAttachFiles] = useState(message.attachFiles || []);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const textContent = message.content || '';

    useEffect(() => {
        if (Utils.isEmptyArray(message.attachFiles) && message.id) {
            fetchAttachFiles();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message.id]);

    const fetchAttachFiles = async () => {
        setIsLoadingFiles(true);
        let stableCount = 0;
        try {
            for (let i = 0; i < Constant.FETCH_ATTACH_FILES.MAX_RETRY; i++) {
                if (i > 0) {
                    await new Promise(resolve => setTimeout(resolve, Constant.FETCH_ATTACH_FILES.RETRY_DELAY));
                }
                try {
                    const response = await getPublicAPI(API, {
                        getApiType: Constant.GET_API_PUBLIC_TYPE.GET_LIST_ATTACH_FILE_BY_AI_CHAT_POST,
                        chatPost: { id: message.id }
                    });
                    const payload = Utils.convertJSONDataToObject(response?.payload);
                    if (!Utils.isEmptyArray(payload)) {
                        setAttachFiles(payload);
                        if (payload.length === stableCount) {
                            return; // skip donot retry 
                        }
                        stableCount = payload.length;
                    }
                } catch (retryErr) {
                    // Continue to next retry
                }
            }
        } finally {
            setIsLoadingFiles(false);
        }
    };

    const imageFiles = attachFiles.filter(file => {
        const fileInfo = Utils.convertJSONDataToObject(file.fileInfo);
        return isImageFile(fileInfo?.fileName);
    });

    const nonImageFiles = attachFiles.filter(file => {
        const fileInfo = Utils.convertJSONDataToObject(file.fileInfo);
        return !isImageFile(fileInfo?.fileName);
    });

    const handleDownloadFile = (file) => {
        const fileInfo = Utils.convertJSONDataToObject(file.fileInfo);
        const s3Path = file.s3Path || fileInfo?.fileUrl;
        downloadFileFromS3({ fileUrl: s3Path, content: fileInfo?.fileName });
    };

    return (
        <div className="chatBox-text message-text">
            <div className="chatBoxContent">
                <span className="chatBox-label">
                    <span className="chatBox-txt">
                        {isLoadingFiles && (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
                                <CircularProgress size={20} />
                            </div>
                        )}
                        {imageFiles.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                                {imageFiles.map((file, index) => {
                                    const fileInfo = Utils.convertJSONDataToObject(file.fileInfo);
                                    const s3Path = file.s3Path || fileInfo?.fileUrl;
                                    const imgUrl = getLinkFileFromS3Path(s3Path);
                                    return (
                                        <div
                                            key={file.id || index}
                                            className="attach-img-chatpost"
                                            style={{ borderRadius: 10, position: 'relative', cursor: 'pointer' }}
                                            onClick={() => handleDownloadFile(file)}
                                        >
                                            <div className="content-post">
                                                <img
                                                    src={imgUrl}
                                                    alt={fileInfo?.fileName || ''}
                                                    style={{ maxWidth: 145, maxHeight: 145, borderRadius: 8, objectFit: 'cover' }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {nonImageFiles.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                                {nonImageFiles.map((file, index) => {
                                    const fileInfo = Utils.convertJSONDataToObject(file.fileInfo);
                                    const fileName = fileInfo?.fileName || '';
                                    const fileSize = fileInfo?.fileSize || 0;
                                    return (
                                        <span
                                            key={file.id || index}
                                            className="file-upload-name file-upload-active"
                                            onClick={() => handleDownloadFile(file)}
                                            style={{ cursor: 'pointer' }}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleDownloadFile(file); }}
                                        >
                                            {`${fileName} (${getFileSizeDisplay(fileSize)})`}
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                        {!Utils.isEmptyString(textContent) && (
                            <div style={{ wordBreak: 'break-word' }}>{textContent}</div>
                        )}
                    </span>
                </span>
            </div>
        </div>
    );
}

export default MsgFilesMessage;
